/* eslint-disable complexity */
import { TMicroApps } from '~/components/Auth';
import { createUserDataFromExternalUserInteractionId } from '~/components/Auth/helpers/index';

export const adminAuthMapper = (
  userAuth: {
    id: string;
    name: string;
    email: string;
    username: string;
    locale: string;
    isWebView: string;
    sessionId: string;
    freshchat: { freshchatId: string; freshchatRestoreId: string };
    tempFeatureConfig: {
      disableFreshchat?: boolean;
      disableDD?: boolean;
      disableWhatfix?: boolean;
      whatfixConfig?: any;
      customFetchEnabled?: boolean;
      customFetchReqOverrideEnabled?: boolean;
      disableSnowplowTracking?: boolean;
      isBerryChatDisabledForAdmin?: boolean;
      disableMixpanelTracking?: boolean;
    };
    state: {
      current: string;
      prev: string;
      timestamp: number;
    };
    features: {
      fullStoryEnabled?: boolean;
    };
    isLoggedIn: boolean;
    company: {
      orgId: any;
      companyType: any;
      id: string;
      name: string;
      url: string;
      type: string;
      lsType?: string;
      companySettings: {
        brandColor?: string;
      };
      companyLogo?: string;
      localeUrls: {
        // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
        [key in TMicroApps]: string;
      };
    };
    roles: string[];
    profileFields?: string[];
  },
  { externalUserInteractionId }: { externalUserInteractionId?: string }
) => {
  let user = {
    id: userAuth.id,
    name: userAuth.name,
    email: userAuth.email,
    username: userAuth.username,
    locale: userAuth.locale,
    isWebView: userAuth.isWebView,
    sessionId: userAuth.sessionId,
    freshchatRestoreId: userAuth.freshchat?.freshchatRestoreId || null,
    freshchatId: userAuth.freshchat?.freshchatId,
    isLoggedIn: userAuth.isLoggedIn,
    currentState: userAuth.state?.current,
    prevState: userAuth.state?.prev,
    stateUpdatedTime: userAuth.state?.timestamp,
    roles: userAuth?.roles,
    profileFields: userAuth?.profileFields || [],
  };
  let featureToggles = {
    isFullstoryEnabled: getBoolean(userAuth.features?.fullStoryEnabled),
    isFreshchatEnabled: !userAuth.tempFeatureConfig?.disableFreshchat,
    isDatadogDisabled: userAuth.tempFeatureConfig?.disableDD || false,
    isWhatfixDisabled: userAuth.tempFeatureConfig?.disableWhatfix || false,
    isBerryChatDisabled: userAuth.tempFeatureConfig?.isBerryChatDisabledForAdmin || false,
    whatfixConfig: userAuth.tempFeatureConfig?.whatfixConfig || {},
    customFetchEnabled: userAuth.tempFeatureConfig?.customFetchEnabled || true,
    customFetchReqOverrideEnabled:
      userAuth.tempFeatureConfig?.customFetchReqOverrideEnabled || false,
    disableSnowplowTracking: userAuth.tempFeatureConfig?.disableSnowplowTracking || false,
    disableMixpanelTracking: userAuth.tempFeatureConfig?.disableMixpanelTracking || false,
  };

  let company = {
    id: userAuth.company.id,
    name: userAuth.company.name,
    logo: userAuth.company.companyLogo,
    learningSite: userAuth.company.url,
    isCustomer: userAuth.company.type === 'CUSTOMER',
    brandColor: userAuth.company.companySettings?.brandColor,
    lsType: userAuth.company.lsType,
    localeUrls: userAuth.company.localeUrls || {},
    companyType: userAuth.company.companyType,
    orgId: userAuth.company.orgId,
  };

  if (externalUserInteractionId && !user.id) {
    const externalUser = createUserDataFromExternalUserInteractionId(externalUserInteractionId);
    user = { ...user, ...externalUser };
  }

  return {
    user,
    featureToggles,
    company,
  };
};

export const learnerAuthMapper = (
  userAuth: {
    id: string;
    name: string;
    sessionId: string;
    isWebView: string;
    email: string;
    username: string;
    locale: string;
    isLoggedIn: boolean;
    tempFeatureConfig: {
      fullStoryEnabled?: boolean;
      disableDD?: boolean;
      disableWhatfix?: boolean;
      whatfixConfig?: any;
      customFetchEnabled?: boolean;
      customFetchReqOverrideEnabled?: boolean;
      disableSnowplowTracking?: boolean;
      isBerryChatDisabledForLearner?: boolean;
      disableMixpanelTracking?: boolean;
    };
    state: {
      current: string;
      prev: string;
      updatedTime: number;
    };
    features: {};
    company: {
      orgId: any;
      companyType: any;
      id: string;
      name: string;
      url: string;
      type: string;
      companySettings: {
        brandColor?: string;
        lsType?: string;
      };
      logoUrl?: string;
      localeUrls: {
        // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
        [key in TMicroApps]: string;
      };
    };
    learnerRoles: string[];
    profileFields?: string[];
  },
  { externalUserInteractionId }: { externalUserInteractionId?: string }
) => {
  let user = {
    id: userAuth.id,
    name: userAuth.name,
    username: userAuth.username,
    sessionId: userAuth.sessionId,
    isWebView: userAuth.isWebView,
    email: userAuth.email,
    locale: userAuth.locale,
    isLoggedIn: userAuth.isLoggedIn,
    currentState: userAuth.state?.current,
    prevState: userAuth.state?.prev,
    stateUpdatedTime: userAuth.state?.updatedTime,
    roles: userAuth.learnerRoles || [],
    profileFields: userAuth?.profileFields || [],
  };

  let featureToggles = {
    isFullstoryEnabled: getBoolean(userAuth.tempFeatureConfig?.fullStoryEnabled),
    isFreshchatEnabled: false,
    isDatadogDisabled: userAuth.tempFeatureConfig?.disableDD || false,
    isWhatfixDisabled: userAuth.tempFeatureConfig?.disableWhatfix || false,
    isBerryChatDisabled: userAuth.tempFeatureConfig?.isBerryChatDisabledForLearner || false,
    whatfixConfig: userAuth.tempFeatureConfig?.whatfixConfig || {},
    customFetchEnabled: userAuth.tempFeatureConfig?.customFetchEnabled || true,
    customFetchReqOverrideEnabled:
      userAuth.tempFeatureConfig?.customFetchReqOverrideEnabled || false,
    disableSnowplowTracking: userAuth.tempFeatureConfig?.disableSnowplowTracking || false,
    disableMixpanelTracking: userAuth.tempFeatureConfig?.disableMixpanelTracking || false,
  };

  let company = {
    id: userAuth.company.id,
    name: userAuth.company.name,
    learningSite: userAuth.company.url,
    logo: userAuth.company.logoUrl,
    isCustomer: userAuth.company.type === 'CUSTOMER',
    brandColor: userAuth.company.companySettings?.brandColor,
    lsType: userAuth.company.companySettings?.lsType,
    localeUrls: userAuth.company.localeUrls || {},
    companyType: userAuth.company.companyType,
    orgId: userAuth.company.orgId,
  };

  if (externalUserInteractionId && !user.id) {
    const externalUser = createUserDataFromExternalUserInteractionId(externalUserInteractionId);
    user = { ...user, ...externalUser };
  }

  return {
    user,
    featureToggles,
    company,
  };
};

function getBoolean(value: string | boolean | undefined) {
  if (value === 'true') {
    return true;
  } else if (value === 'false') {
    return false;
  }
  return !!value;
}
